import { blinkConstants } from '@thoughtspot/blink-constants';
import { CustomStylingConfig } from '@thoughtspot/custom-style-service';
import { jsonConstants } from '@thoughtspot/json-constants';
import ol from '@thoughtspot/openlayers';
import chroma from 'chroma-js';
import _ from 'lodash';
import * as topojson from 'topojson-client';
import { generateDarkModePalette, isDarkModeActive, } from '@thoughtspot/radiant-react/widgets/color/dark-mode-color-utils';
import { getChartType, getColorBySerieName, getColorPaletteFromLegendOverrides, getColorScaleOfMultiColorSeriesStrategy, getMultiColorSeriesColors, getSeriesColorsMap, getUpdatedSeriesColor, getVisualizedColumns, getXAxisColumnsHash, setColorScaleOfMultiColorSeriesColors, transformGradientColorsWithTransparentMidpoint, } from '../../core/chart-model-utils';
import { GeoConfigType } from '../../core/chart-ui-utils';
import { ChartType } from '../../types';
import { CUSTOM_REGION_PREVIEW_FILL_COLOR, CUSTOM_REGION_PREVIEW_LABEL_FONT_STYLE, CUSTOM_REGION_PREVIEW_STROKE_COLOR, CUSTOM_REGION_PREVIEW_STROKE_WIDTH, DEFAULT_FEATURE_READ_BATCH_SIZE, GEO_3D_PROJECTION_TYPES, GEO_AREA_MAX_OPACITY, GEO_AREA_OPACITY, MIN_BUBBLE_RADIUS, VIEW_PROJECTION, } from './geo-constants';
import GeoCountryConfig from './geo-country-config';
import GeoPropField from './metadata-services/geo-prop-field';
export class GeoUtils {
    static getAreaOfExtent(extent) {
        let area = 0;
        if (!ol.extent.isEmpty(extent)) {
            area = ol.extent.getWidth(extent) * ol.extent.getHeight(extent);
        }
        return area;
    }
    static triangulateGrid(numRows, numCols, triangleIndices, vertexOffset) {
        if (vertexOffset === void 0) {
            vertexOffset = 0;
        }
        if (!triangleIndices) {
            const numTriangles = numRows * numCols * 2;
            triangleIndices = new Uint16Array(numTriangles * 3);
        }
        let i;
        let row;
        let col;
        let topLeft;
        let topRight;
        let bottomLeft;
        let bottomRight;
        let triangleVertexIndex;
        for (i = 0, triangleVertexIndex = 0; i < numRows * numCols; ++i) {
            row = Math.floor(i / numCols);
            col = i % numCols;
            topLeft = vertexOffset + row * (numCols + 1) + col;
            topRight = topLeft + 1;
            bottomLeft = topLeft + numCols + 1;
            bottomRight = bottomLeft + 1;
            triangleIndices[triangleVertexIndex++] = topLeft;
            triangleIndices[triangleVertexIndex++] = bottomLeft;
            triangleIndices[triangleVertexIndex++] = topRight;
            triangleIndices[triangleVertexIndex++] = topRight;
            triangleIndices[triangleVertexIndex++] = bottomLeft;
            triangleIndices[triangleVertexIndex++] = bottomRight;
        }
        return triangleIndices;
    }
    static isProjectionGlobe(projectionType) {
        return projectionType === GEO_3D_PROJECTION_TYPES.GLOBE;
    }
    static isProjectionMap(projectionType) {
        return projectionType === GEO_3D_PROJECTION_TYPES.MAP;
    }
    static isProjectionPerspectivePlane(projectionType) {
        return projectionType === GEO_3D_PROJECTION_TYPES.PERSPECTIVE_PLANE;
    }
    static convertLatLongToXYZMercator(lat, lng, alt) {
        if (alt === void 0) {
            alt = 0;
        }
        const x = (this.constants.MAP_WIDTH /
            (this.constants.MAP_MAX_LONGITUDE -
                this.constants.MAP_MIN_LONGITUDE)) *
            lng;
        const latRad = (lat * Math.PI) / 180.0;
        const mercN = Math.log(Math.tan(Math.PI / 4.0 + latRad / 2.0));
        const y = this.constants.MAP_HEIGHT / 2.0 -
            (this.constants.MAP_HEIGHT / 2.0 -
                (this.constants.MAP_WIDTH * mercN) / (2.0 * Math.PI));
        return {
            x,
            y,
            z: alt,
        };
    }
    static convertLatLongToXYZSpherical(lat, lng, alt) {
        if (alt === void 0) {
            alt = 0;
        }
        const phi = ((90.0 - lat) * Math.PI) / 180.0;
        const theta = ((180.0 - lng) * Math.PI) / 180.0 + 180.0;
        const sinPhi = Math.sin(phi);
        const cosPhi = Math.cos(phi);
        const sinTheta = Math.sin(theta);
        const cosTheta = Math.cos(theta);
        const x = this.constants.EARTH_RADIUS * sinPhi * cosTheta;
        const y = this.constants.EARTH_RADIUS * cosPhi;
        const z = this.constants.EARTH_RADIUS * sinPhi * sinTheta;
        const altFactor = (1.0 + alt) / this.constants.EARTH_RADIUS;
        return {
            x: x * altFactor,
            y: y * altFactor,
            z: z * altFactor,
        };
    }
    static computeBestColorScalingFunction(dataFractions) {
        dataFractions = dataFractions.slice(0);
        const identityFunc = (number) => number;
        const squareFunc = (number) => number * number;
        const cubeFunc = (number) => number * number * number;
        let maxCoefficient = GeoUtils.computeDistributionCoefficient(identityFunc, dataFractions);
        let bestScalingFunc = identityFunc;
        let coefficient = GeoUtils.computeDistributionCoefficient(squareFunc, dataFractions);
        if (coefficient > maxCoefficient) {
            maxCoefficient = coefficient;
            bestScalingFunc = squareFunc;
            coefficient = GeoUtils.computeDistributionCoefficient(cubeFunc, dataFractions);
            if (coefficient > maxCoefficient) {
                maxCoefficient = coefficient;
                bestScalingFunc = cubeFunc;
            }
        }
        else {
            coefficient = GeoUtils.computeDistributionCoefficient(Math.sqrt, dataFractions);
            if (coefficient > maxCoefficient) {
                maxCoefficient = coefficient;
                bestScalingFunc = Math.sqrt;
                coefficient = GeoUtils.computeDistributionCoefficient(Math.cbrt, dataFractions);
                if (coefficient > maxCoefficient) {
                    maxCoefficient = coefficient;
                    bestScalingFunc = Math.cbrt;
                }
            }
        }
        return bestScalingFunc;
    }
    static getInteriorPoint(feature) {
        if (!feature.getGeometry()) {
            throw new Error(`Missing geometry for feature:${feature.get(GeoPropField.GEOID.toString())}`);
        }
        if (feature.getGeometry().getType() === 'MultiPolygon') {
            return GeoUtils.getLargestPolygon(feature.getGeometry().getPolygons()).getInteriorPoint();
        }
        if (feature.getGeometry().getType() === 'Polygon') {
            return feature.getGeometry().getInteriorPoint();
        }
        if (feature.getGeometry().getType() === 'Point') {
            return feature.getGeometry();
        }
        throw new Error(`Unhandled geometry type${feature.getGeometry().getType()}`);
    }
    static getLargestPolygon(polygons) {
        const polyObj = [];
        for (let i = 0; i < polygons.length; i++) {
            polyObj.push({
                polygon: polygons[i],
                area: polygons[i].getArea(),
            });
        }
        polyObj.sort(function (a, b) {
            return a.area - b.area;
        });
        return polyObj[polyObj.length - 1].polygon;
    }
    static assignSeriesColors(chartModel, series, gradientColors) {
        switch (getChartType(chartModel)) {
            case ChartType.GeoArea:
                GeoUtils.assignGeoAreaSeriesColors(chartModel, series, gradientColors);
                break;
            case ChartType.GeoBubble:
                GeoUtils.assignGeoBubbleSeriesColors(chartModel, series);
                break;
            case ChartType.GeoHeatmap:
                break;
            default:
                throw new Error('Unhandled geo chart type');
        }
    }
    static updateRenderedGeoMapColorsInModel(chartModel, blinkGeoMap, updateClientStateProperty) {
        let shouldUpdateChartProperties = false;
        switch (getChartType(chartModel)) {
            case ChartType.GeoArea:
                let systemMultiColorSeriesColors = getMultiColorSeriesColors(chartModel);
                const xAxisColumnHash = getXAxisColumnsHash(chartModel);
                blinkGeoMap.getSeries().forEach(serie => {
                    var _a, _b;
                    const seriesColorMap = (_a = systemMultiColorSeriesColors.find(colMap => serie.blinkSeriesId &&
                        colMap.serieName ===
                            serie.blinkSeriesId.toLowerCase())) === null || _a === void 0 ? void 0 : _a.colorMap;
                    const xAxisHashAlreadyPresent = seriesColorMap
                        ? !!seriesColorMap.find(colMap => colMap.serieName === xAxisColumnHash)
                        : true;
                    if (!!systemMultiColorSeriesColors &&
                        (!seriesColorMap ||
                            (seriesColorMap && !xAxisHashAlreadyPresent))) {
                        shouldUpdateChartProperties = true;
                        systemMultiColorSeriesColors = setColorScaleOfMultiColorSeriesColors(systemMultiColorSeriesColors, serie.blinkSeriesId, xAxisColumnHash, (_b = serie.lightScale) !== null && _b !== void 0 ? _b : serie.scale);
                    }
                });
                if (shouldUpdateChartProperties ||
                    !!getColorPaletteFromLegendOverrides(chartModel)) {
                    const clientStateToBeUpdated = _.cloneDeep(chartModel.vizProp);
                    updateClientStateProperty(clientStateToBeUpdated, [
                        jsonConstants.chartClientStateKeys
                            .SYSTEM_MULTI_COLOR_SERIES_COLORS,
                    ], systemMultiColorSeriesColors, !!getColorPaletteFromLegendOverrides(chartModel));
                }
                break;
            case ChartType.GeoBubble:
                let systemSeriesColors = chartModel.vizProp.systemSeriesColors
                    ? [...chartModel.vizProp.systemSeriesColors]
                    : [];
                blinkGeoMap.getSeries().forEach(serie => {
                    var _a, _b;
                    const systemSerieColor = (_a = _.find(systemSeriesColors, colMap => serie.blinkSeriesId &&
                        colMap.serieName ===
                            serie.blinkSeriesId.toLowerCase())) === null || _a === void 0 ? void 0 : _a.color;
                    if (systemSeriesColors.length === 0 || !systemSerieColor) {
                        shouldUpdateChartProperties = true;
                        systemSeriesColors = getUpdatedSeriesColor(systemSeriesColors, serie.blinkSeriesId, (_b = serie.lightColor) !== null && _b !== void 0 ? _b : serie.color);
                    }
                });
                if (shouldUpdateChartProperties ||
                    !!getColorPaletteFromLegendOverrides(chartModel)) {
                    const clientStateToBeUpdated = _.cloneDeep(chartModel.vizProp);
                    updateClientStateProperty(clientStateToBeUpdated, [
                        jsonConstants.chartClientStateKeys
                            .SYSTEM_SERIES_COLORS,
                    ], systemSeriesColors, !!getColorPaletteFromLegendOverrides(chartModel));
                }
                break;
            case ChartType.GeoHeatmap:
                break;
            default:
                throw new Error('Unhandled geo chart type');
        }
    }
    static isGeoConfigWithPartialDrawing(geoConfig) {
        if (!(geoConfig === null || geoConfig === void 0 ? void 0 : geoConfig.getParent())) {
            return false;
        }
        return (geoConfig &&
            (geoConfig.getType() === GeoConfigType.ZIP_CODE ||
                geoConfig.getType() === GeoConfigType.ADMIN_DIV_2));
    }
    static getPropKeysForMatching(configType, country) {
        let props;
        switch (configType) {
            case GeoConfigType.ADMIN_DIV_0:
                props = [
                    GeoPropField.NAME,
                    GeoPropField.NAME_LONG,
                    GeoPropField.NAME_SORT,
                    GeoPropField.ADMIN,
                    GeoPropField.FORMAL_EN,
                    GeoPropField.GEOID,
                    GeoPropField.ISO_A3,
                ];
                break;
            case GeoConfigType.ADMIN_DIV_1:
            case GeoConfigType.ADMIN_DIV_2:
                props = GeoCountryConfig.get(country)
                    .getExtraFieldsToMatch(configType)
                    .map((fieldName) => {
                    return new GeoPropField(fieldName);
                })
                    .concat([GeoPropField.GEOID, GeoPropField.NAME]);
                break;
            case GeoConfigType.ZIP_CODE:
            case GeoConfigType.CUSTOM_REGION:
                props = [GeoPropField.GEOID];
                break;
            case GeoConfigType.POINT:
                props = [GeoPropField.LATLONG];
                break;
            default:
                props = [GeoPropField.NAME, GeoPropField.GEOID];
                break;
        }
        return props;
    }
    static getTopoGlURLForGeoType(geoType) {
        let fileName;
        switch (geoType) {
            case GeoConfigType.ZIP_CODE:
                fileName = 'us_zip';
                break;
            case GeoConfigType.ADMIN_DIV_1:
                fileName = 'us_state';
                break;
            case GeoConfigType.ADMIN_DIV_0:
                fileName = 'world';
                break;
            default:
                fileName = null;
        }
        if (fileName === null) {
            return null;
        }
        return `/resources/geo/topogl/${fileName}.topogl.bin`;
    }
    static getScaleFromColor(color) {
        return [
            chroma(color)
                .brighten()
                .hex(),
            color,
            chroma(color)
                .darken()
                .hex(),
        ];
    }
    static getOverrideColorsForGeoArea(overridePalette) {
        const { COLOR_VARIATION_COUNT } = blinkConstants.colorLayout;
        const hueCount = Math.floor(overridePalette.length / COLOR_VARIATION_COUNT);
        return Array.from({ length: hueCount }, (_, i) => overridePalette
            .slice(i * COLOR_VARIATION_COUNT, (i + 1) * COLOR_VARIATION_COUNT)
            .filter(Boolean));
    }
    static getOverrideColorsForGeoBubble(overridePalette) {
        const { COLOR_COUNT_DEFAULT, COLOR_VARIATION_COUNT, } = blinkConstants.colorLayout;
        return Array.from({ length: COLOR_COUNT_DEFAULT }, (_, i) => overridePalette[i * COLOR_VARIATION_COUNT + 2]).filter(Boolean);
    }
    static assignGeoAreaSeriesColors(chartModel, series, gradientColors) {
        const overridePalette = getColorPaletteFromLegendOverrides(chartModel);
        if (overridePalette && !gradientColors) {
            const overrideAreaColors = GeoUtils.getOverrideColorsForGeoArea(overridePalette);
            let updatedMultiColorSeriesColors = [
                ...(chartModel.vizProp.multiColorSeriesColors || []),
            ];
            let updatedSystemMultiColorSeriesColors = [
                ...(chartModel.vizProp.systemMultiColorSeriesColors || []),
            ];
            const columnHash = getXAxisColumnsHash(chartModel);
            series.forEach((serie, index) => {
                const { blinkSeriesId } = serie;
                const overrideScale = overrideAreaColors[index % overrideAreaColors.length];
                serie.scale = overrideScale;
                serie.color =
                    overrideScale[Math.floor(overrideScale.length / 2)];
                updatedMultiColorSeriesColors = updatedMultiColorSeriesColors.filter(serieMap => serieMap.serieName !== blinkSeriesId.toLowerCase());
                updatedSystemMultiColorSeriesColors = setColorScaleOfMultiColorSeriesColors(updatedSystemMultiColorSeriesColors, blinkSeriesId, columnHash, overrideScale);
            });
            _.set(chartModel, 'vizProp', {
                ...chartModel.vizProp,
                multiColorSeriesColors: updatedMultiColorSeriesColors,
                systemMultiColorSeriesColors: updatedSystemMultiColorSeriesColors,
            });
            return;
        }
        const savedAreaColors = getMultiColorSeriesColors(chartModel);
        const allAreaColors = CustomStylingConfig.getGeoAreaColors();
        const userPickedAreaSeries = new Set((chartModel.vizProp.multiColorSeriesColors || [])
            .map(colMap => { var _a; return (_a = colMap === null || colMap === void 0 ? void 0 : colMap.serieName) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
            .filter(Boolean));
        const dark = isDarkModeActive();
        series.forEach((serie, index) => {
            const columnHash = getXAxisColumnsHash(chartModel);
            const colorScale = getColorScaleOfMultiColorSeriesStrategy(savedAreaColors, serie.blinkSeriesId, columnHash)(transformGradientColorsWithTransparentMidpoint(gradientColors));
            const lightScale = colorScale
                ? [...colorScale]
                : allAreaColors[index % allAreaColors.length];
            const isUserPicked = !!serie.blinkSeriesId &&
                userPickedAreaSeries.has(serie.blinkSeriesId.toLowerCase());
            const renderScale = dark && !isUserPicked
                ? generateDarkModePalette(lightScale)
                : lightScale;
            serie.lightScale = lightScale;
            serie.scale = renderScale;
            serie.color = renderScale[Math.floor(renderScale.length / 2)];
            serie.lightColor = lightScale[Math.floor(lightScale.length / 2)];
        });
    }
    static assignGeoBubbleSeriesColors(chartModel, series) {
        const overridePalette = getColorPaletteFromLegendOverrides(chartModel);
        if (overridePalette) {
            const overrideGeoBubbleColors = GeoUtils.getOverrideColorsForGeoBubble(overridePalette);
            let updatedSeriesColors = [
                ...(chartModel.vizProp.seriesColors || []),
            ];
            let updatedSystemSeriesColors = [
                ...(chartModel.vizProp.systemSeriesColors || []),
            ];
            const columnIds = getVisualizedColumns(chartModel).map(col => col.id);
            series.forEach((serie, index) => {
                const { blinkSeriesId } = serie;
                if (blinkSeriesId === undefined ||
                    columnIds.includes(blinkSeriesId)) {
                    return;
                }
                const colorOverride = overrideGeoBubbleColors[index % overrideGeoBubbleColors.length];
                serie.color = colorOverride;
                updatedSeriesColors = updatedSeriesColors.filter(serieMap => serieMap.serieName !== blinkSeriesId.toLowerCase());
                updatedSystemSeriesColors = getUpdatedSeriesColor(updatedSystemSeriesColors, blinkSeriesId, colorOverride);
            });
            _.set(chartModel, 'vizProp', {
                ...chartModel.vizProp,
                seriesColors: updatedSeriesColors,
                systemSeriesColors: updatedSystemSeriesColors,
            });
            return;
        }
        const savedBubbleColors = getSeriesColorsMap(chartModel);
        const allBubbleColors = CustomStylingConfig.getGeoBubbleColors();
        const userPickedBubbleSeries = new Set((chartModel.vizProp.seriesColors || [])
            .map(colMap => { var _a; return (_a = colMap === null || colMap === void 0 ? void 0 : colMap.serieName) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
            .filter(Boolean));
        const dark = isDarkModeActive();
        series.forEach((serie, index) => {
            const savedBubbleColor = getColorBySerieName(savedBubbleColors, serie.blinkSeriesId);
            const lightColor = !_.isNil(savedBubbleColor)
                ? savedBubbleColor
                : allBubbleColors[index % allBubbleColors.length];
            const isUserPicked = !!serie.blinkSeriesId &&
                userPickedBubbleSeries.has(serie.blinkSeriesId.toLowerCase());
            serie.lightColor = lightColor;
            serie.color =
                dark && !isUserPicked
                    ? generateDarkModePalette([lightColor])[0]
                    : lightColor;
        });
    }
    static computeDistributionCoefficient(scalingFunction, fractions) {
        fractions = fractions.sort().map(function (f) {
            return scalingFunction(f);
        });
        let sum = 0;
        for (let i = 1; i < fractions.length; i++) {
            const diff = fractions[i] - fractions[i - 1];
            sum += diff * diff;
        }
        const coefficient = 1 / sum;
        return coefficient;
    }
    static filterInvalidFeatures(features) {
        return features.filter(feature => !!feature.geometry);
    }
    static getCustomFeatures(topojsonContent) {
        const objectKey = Object.keys(topojsonContent.objects)[0];
        const geojsonContent = topojson.feature(topojsonContent, objectKey);
        const featuresToRead = geojsonContent === null || geojsonContent === void 0 ? void 0 : geojsonContent.features;
        let features = [];
        while ((featuresToRead === null || featuresToRead === void 0 ? void 0 : featuresToRead.length) > 0) {
            const currentBatchFeatures = featuresToRead.splice(0, DEFAULT_FEATURE_READ_BATCH_SIZE);
            geojsonContent.features = GeoUtils.filterInvalidFeatures(currentBatchFeatures);
            features = features.concat(new ol.format.GeoJSON().readFeatures(geojsonContent, {
                featureProjection: VIEW_PROJECTION,
            }));
        }
        return features;
    }
}
GeoUtils.constants = {
    PROJECTION_TRANSITION_DURATION: 512,
    DATA_POINT_HIGHLIGHT_TRANSITION_DURATION: 400,
    MAP_MIN_LONGITUDE: -180,
    MAP_MAX_LONGITUDE: 180,
    MAP_MIN_LATITUDE: -85,
    MAP_MAX_LATITUDE: 85,
    MAP_MAX_LATITUDE_MERCATOR: 85.051129,
    MAP_MIN_LATITUDE_MERCATOR: -85.051129,
    MAP_WIDTH: 4,
    MAP_HEIGHT: 4,
    EARTH_RADIUS: 1,
    GEOBOUNDS: {
        WORLD: [-64, -180, 86, 180],
        CONTINENTAL_US: [24.396308, -124.848974, 49.384358, -66.885444],
    },
};
GeoUtils.customRegionPreviewStyles = {
    polygonStyle: new ol.style.Style({
        fill: new ol.style.Fill({
            color: CUSTOM_REGION_PREVIEW_FILL_COLOR,
        }),
        stroke: new ol.style.Stroke({
            color: CUSTOM_REGION_PREVIEW_STROKE_COLOR,
            width: CUSTOM_REGION_PREVIEW_STROKE_WIDTH,
        }),
    }),
    pointStyle: new ol.style.Style({
        image: new ol.style.Circle({
            radius: MIN_BUBBLE_RADIUS,
            fill: new ol.style.Fill({
                color: CUSTOM_REGION_PREVIEW_FILL_COLOR,
            }),
            stroke: new ol.style.Stroke({
                color: CUSTOM_REGION_PREVIEW_STROKE_COLOR,
                width: CUSTOM_REGION_PREVIEW_STROKE_WIDTH,
            }),
        }),
    }),
    labelStyle: new ol.style.Style({
        text: new ol.style.Text({
            font: CUSTOM_REGION_PREVIEW_LABEL_FONT_STYLE,
            fill: new ol.style.Fill({
                color: CUSTOM_REGION_PREVIEW_STROKE_COLOR,
            }),
            stroke: new ol.style.Stroke({
                color: CUSTOM_REGION_PREVIEW_FILL_COLOR,
                width: CUSTOM_REGION_PREVIEW_STROKE_WIDTH,
            }),
        }),
    }),
};
GeoUtils.olSourceState = {
    READY: 'ready',
};
GeoUtils.getGeoAreaOpacity = (isGradientColoringEnabled) => isGradientColoringEnabled ? GEO_AREA_MAX_OPACITY : GEO_AREA_OPACITY;
//# sourceMappingURL=geo-utils.js.map